<template>
    <div
        class="relative h-screen w-full overflow-hidden p-(--layout-padding-around-islands) [background:var(--layout-background-gradient-1),var(--layout-background-gradient-2),var(--layout-background-gradient-3)] before:pointer-events-none before:fixed before:top-0 before:left-0 before:z-(--layout-background-texture-z-index) before:h-full before:w-full before:bg-(image:--layout-background-texture) before:opacity-(--layout-background-texture-opacity) before:content-['']">
        <Motion
            class="fixed top-0 left-0 z-40 h-screen w-screen bg-(--slate-overlay-alpha-45) md:pointer-events-none! md:opacity-0!"
            :initial="false"
            :animate="sidebarMotionState"
            :variants="DashboardBackdropVariants"
            @click="closeSidebar" />

        <Motion
            as="aside"
            class="fixed top-(--layout-padding-around-islands) bottom-(--layout-padding-around-islands) left-0 z-50 w-77.5 pr-6 will-change-transform md:left-(--layout-padding-around-islands)!"
            :initial="false"
            :variants="DashboardSidebarVariants"
            :animate="sidebarMotionState"
            :transition="DASHBOARD_LAYOUT_TRANSITION">
            <Sidebar />
        </Motion>

        <div
            class="relative h-full overflow-hidden rounded-(--border-radius-xl) [--dashboard-content-offset:0rem] md:[--dashboard-content-offset:19.375rem]">
            <Motion
                class="absolute inset-0 will-change-transform"
                :initial="false"
                :animate="sidebarMotionState"
                :variants="DashboardContentShellVariants"
                :transition="DASHBOARD_LAYOUT_TRANSITION">
                <GlassSurface class="h-full w-full rounded-(--border-radius-xl) contain-[layout_paint]" />
            </Motion>

            <Motion
                :class="[
                    'relative z-10 box-border flex h-full min-w-0 flex-col dashboard-content-resize',
                    isSidebarOpen ? 'w-[calc(100%-var(--dashboard-content-offset))]' : 'w-full'
                ]"
                :initial="false"
                :animate="sidebarMotionState"
                :variants="DashboardContentShellVariants"
                :transition="DASHBOARD_LAYOUT_TRANSITION">
                <MainContentIslandHeader />
                <main
                    class="overflow-y-auto p-8 scrollbar-gutter-stable scrollbar-thin [scrollbar-color:var(--gray-alpha-50)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-(--gray-alpha-50) [&::-webkit-scrollbar-track]:bg-transparent max-sm:scrollbar-gutter-auto">
                    <slot />
                </main>
            </Motion>
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { Variant, Transition } from 'motion-v';

    import { BRAND } from '@constants/common';
    import { MotionDuration, MotionEase, MotionType, Opacity, PointerEvents } from '@constants/motion/defaults';
    import { UIState, type UIStateType } from '@constants/ui/ui-state';
    import { formatPageTitle } from '@utils/formatter';
    const DASHBOARD_LAYOUT_TRANSITION = {
        type: MotionType.Tween,
        duration: MotionDuration.Height,
        ease: MotionEase.Smooth
    } satisfies Transition;

    const DashboardSidebarVariants = Object.freeze({
        [UIState.Expanded]: { transform: 'translate3d(0%, 0, 0)' },
        [UIState.Collapsed]: { transform: 'translate3d(-100%, 0, 0)' }
    } satisfies Record<UIStateType, Variant>);

    const DashboardContentShellVariants = Object.freeze({
        [UIState.Expanded]: { transform: 'translate3d(var(--dashboard-content-offset), 0, 0)' },
        [UIState.Collapsed]: { transform: 'translate3d(0rem, 0, 0)' }
    } satisfies Record<UIStateType, Variant>);

    const DashboardBackdropVariants = Object.freeze({
        [UIState.Expanded]: {
            opacity: Opacity.Visible,
            pointerEvents: PointerEvents.Auto
        },
        [UIState.Collapsed]: {
            opacity: Opacity.Hidden,
            pointerEvents: PointerEvents.None
        }
    } satisfies Record<UIStateType, Variant>);

    const route = useRoute();
    useHead(() => {
        const routeTitle = typeof route.meta.title === 'string' ? route.meta.title : undefined;

        return { title: formatPageTitle(BRAND, routeTitle) };
    });

    const { isSidebarOpen, closeSidebar } = useSidebar();

    const sidebarMotionState = computed<UIStateType>(() =>
        isSidebarOpen.value ? UIState.Expanded : UIState.Collapsed
    );
</script>
